// src/pages/AnaKategorilerPage.js
import React, { useMemo, useState, useEffect } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";
import {
  List,
  Datagrid,
  BooleanField,
  EditButton,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRecordContext,
  useRefresh,
  useNotify,
  useRedirect,
  TextField,
  EditBase,
  useEditContext,
  useUpdate,
  FunctionField,
} from "react-admin";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AddCircle as AddCircleIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import ResimUpload from "../ResimUpload";
import { API_BASE } from "../config"; // (UPLOAD_BASE'a gerek yok; aşağıda API_ORIGIN kullanacağız)
// API_BASE: https://.../api  ->  BACKEND_ORIGIN: https://...
const BACKEND_ORIGIN = API_BASE.replace(/\/api\/?$/, "");



// --- GÖRSEL URL YARDIMCILARI ---
const extractUploadsPath = (u) => {
  if (!u) return '';
  const s = String(u).trim();
  // Boşluk/özel karakterlerde kesmeden yakalar, ?query’yi atar
  const m = s.match(/\/uploads\/[^\)"'\\]+/i);
  if (m) return m[0].split('?')[0];
  if (s.startsWith('uploads/')) return '/' + s.replace(/^\/+/, '').split('?')[0];
  return s.split('?')[0];
};


const toPublicUrl = (u) => {
  const p = extractUploadsPath(u);
  if (!p) return '';
  // '/uploads/...'(ya da 'uploads/...') -> sadece dosya yolu
  const rel = String(p).replace(/^\/?uploads\/?/, '');
  // Her path segmentini encode et (boşluk, TR karakterler vb.)
  const encodedRel = rel.split('/').map(encodeURIComponent).join('/');
  // API ile aynı origin’den servis et (HTTPS/localhost karmaşası biter)
  return `${BACKEND_ORIGIN}/uploads/${encodedRel}`;
};



const PLACEHOLDER = "Bu dilde içerik mevcut değildir.";
const ALLOWED = ["tr", "en", "de"];

/* --- UI TEXTS (TR/EN/DE) --- */
const T = {
  tr: {
    listTitle: "📚 Ana Kategoriler",
    newBtn: "Yeni Ana Kategori",
    exportBtn: "Dışa Aktar",
    save: "Kaydet",
    saving: "Kaydediliyor…",
    cancel: "Vazgeç",
    editTitle: "Ana Kategori Düzenle",
    updatedOk: "✅ Güncelleme başarılı",
    uploadImage: "Görsel Yükle",

    image: "Resim",
    title: "Başlık",
    
    order: "🔢 Sıra",
    status: "Durum",
    contentLang: "İçerik Dili",
    contentLangShort: "İçerik Dili",
    edit: "Düzenle",
    delete: "Sil",
    confirmDelete: "Silmek istediğinize emin misiniz?",
    createTitle: "➕ Yeni Ana Kategori Ekle",
    createdOk: "✅ Ana kategori başarıyla oluşturuldu",
    linkLbl: "🔗 Link (https://...)",
    imageUrl: "🖼️ Görsel URL",
    langField: "🌐 İçerik Dili (tr/en/de)",
    active: "Aktif",
    passive: "Pasif",
    trTitle: "🇹🇷 Başlık (Türkçe)",
    enTitle: "🇬🇧 Title (İngilizce)",
    deTitle: "🇩🇪 Titel (Almanca)",
    trDesc: "🇹🇷 Açıklama (Türkçe)",
    enDesc: "🇬🇧 Description (İngilizce)",
    deDesc: "🇩🇪 Beschreibung (Almanca)",

    /* Karşılama (etkinliklerdeki formatla aynı) */
    emptyTitle: "Ana kategoriler eklemeye hoş geldiniz",
    emptyDesc:
      "Buraya ekleyeceğiniz görseller ve metinler uygulamanızın ana ekranındaki kategoriler bölümünde yayınlanacaktır.",
    emptyBtn: "Hemen Ana Kategori Ekle",

    langNames: { tr: "Türkçe", en: "İngilizce", de: "Almanca" },
  },
  en: {
    listTitle: "📚 Main Categories",
    newBtn: "New Main Category",
    exportBtn: "Export",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    editTitle: "Edit Main Category",
    updatedOk: "✅ Updated successfully",
    uploadImage: "Upload Image",

    image: "Image",
    title: "Title",
    
    order: "🔢 Order",
    status: "Status",
    contentLang: "Content Language",
    contentLangShort: "Content Language",
    edit: "Edit",
    delete: "Delete",
    confirmDelete: "Are you sure you want to delete?",
    createTitle: "➕ Create Main Category",
    createdOk: "✅ Main category created",
   
    imageUrl: "🖼️ Image URL",
    langField: "🌐 Content Language (tr/en/de)",
    active: "Active",
    passive: "Inactive",
    trTitle: "🇹🇷 Title (Turkish)",
    enTitle: "🇬🇧 Title (English)",
    deTitle: "🇩🇪 Title (German)",
    trDesc: "🇹🇷 Description (Turkish)",
    enDesc: "🇬🇧 Description (English)",
    deDesc: "🇩🇪 Description (German)",

    emptyTitle: "Welcome to adding main categories",
    emptyDesc:
      "Items you add here will appear in the main categories section of your app’s home screen.",
    emptyBtn: "Add Main Category",

    langNames: { tr: "Turkish", en: "English", de: "German" },
  },
  de: {
    listTitle: "📚 Hauptkategorien",
    newBtn: "Neue Hauptkategorie",
    exportBtn: "Exportieren",
    save: "Speichern",
    saving: "Wird gespeichert…",
    cancel: "Abbrechen",
    editTitle: "Hauptkategorie bearbeiten",
    updatedOk: "✅ Erfolgreich aktualisiert",
    uploadImage: "Bild hochladen",

    image: "Bild",
    title: "Titel",
   
    order: "🔢 Reihenfolge",
    status: "Status",
    contentLang: "Inhaltssprache",
    contentLangShort: "Inhaltssprache",
    edit: "Bearbeiten",
    delete: "Löschen",
    confirmDelete: "Möchten Sie wirklich löschen?",
    createTitle: "➕ Hauptkategorie erstellen",
    createdOk: "✅ Hauptkategorie erstellt",
    
    imageUrl: "🖼️ Bild-URL",
    langField: "🌐 Inhaltssprache (tr/en/de)",
    active: "Aktiv",
    passive: "Inaktiv",
    trTitle: "🇹🇷 Titel (Türkisch)",
    enTitle: "🇬🇧 Titel (Englisch)",
    deTitle: "🇩🇪 Titel (Deutsch)",
    trDesc: "🇹🇷 Beschreibung (Türkisch)",
    enDesc: "🇬🇧 Beschreibung (Englisch)",
    deDesc: "🇩🇪 Beschreibung (Deutsch)",

    emptyTitle: "Willkommen zum Hinzufügen von Hauptkategorien",
    emptyDesc:
      "Elemente, die Sie hier hinzufügen, erscheinen im Bereich der Hauptkategorien auf der Startseite Ihrer App.",
    emptyBtn: "Hauptkategorie hinzufügen",

    langNames: { tr: "Türkisch", en: "Englisch", de: "Deutsch" },
  },
};

function useUiTexts() {
  const [uiLang, setUiLang] = useState(
    () => (localStorage.getItem("app_lang") || "tr").slice(0, 2).toLowerCase()
  );
  useEffect(() => {
    const sync = () => {
      const l = (localStorage.getItem("app_lang") || "tr")
        .slice(0, 2)
        .toLowerCase();
      setUiLang(ALLOWED.includes(l) ? l : "tr");
    };
    window.addEventListener("app_lang_change", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("app_lang_change", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);
  return useMemo(() => T[ALLOWED.includes(uiLang) ? uiLang : "tr"], [uiLang]);
}

/* ---------- Actions bar ---------- */
const ListActions = () => {
  const TT = useUiTexts();
  return (
    <TopToolbar sx={{ gap: 1.5, px: 0, width: "100%", justifyContent: "flex-end" }}>
      <CreateButton
        icon={<AddCircleIcon />}
        label={TT.newBtn}
        sx={{
          px: 3,
          py: 1.35,
          fontSize: "1.05rem",
          fontWeight: 800,
          borderRadius: "6px",
          textTransform: "none",
          letterSpacing: "0.2px",
          bgcolor: "#f59e0b !important",
          color: "#fff !important",
          boxShadow: "0 6px 18px rgba(245,158,11,0.28)",
          "&:hover": { bgcolor: "#d97706 !important" },
        }}
      />
      <ExportButton
        icon={<FileDownloadIcon />}
        label={TT.exportBtn}
        sx={{
          px: 2.25,
          py: 1.1,
          fontSize: "1rem",
          fontWeight: 700,
          borderRadius: "6px",
          textTransform: "none",
          letterSpacing: "0.2px",
          bgcolor: "#2563eb !important",
          color: "#fff !important",
          boxShadow: "0 6px 18px rgba(37,99,235,0.22)",
          "&:hover": { bgcolor: "#1d4ed8 !important" },
        }}
      />
    </TopToolbar>
  );
};

/* ---------- Delete button ---------- */
const CustomDeleteButton = ({ record }) => {
  const TT = useUiTexts();
  const refresh = useRefresh();
  const handleDelete = async () => {
    const confirmed = window.confirm(TT.confirmDelete);
    if (!confirmed) return;
    const token = localStorage.getItem("token");
await fetch(`${API_BASE}/anakategoriler/${record.id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    refresh();
  };
  return (
    <button
      onClick={handleDelete}
      style={{
        color: "#fff",
        background: "#ef4444",
        border: "1px solid #ef4444",
        borderRadius: 6,
        padding: "6px 10px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontWeight: 700,
        fontSize: "0.95rem",
      }}
      title={TT.delete}
    >
      <DeleteIcon fontSize="small" /> {TT.delete}
    </button>
  );
};

const ActionButtons = () => {
  const TT = useUiTexts();
  const record = useRecordContext();
  return (
    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-start" }}>
      <EditButton
        record={record}
        icon={<EditIcon />}
        sx={{
          color: "#fff !important",
          bgcolor: "#f59e0b !important",
          border: "1px solid #f59e0b",
          borderRadius: "6px",
          px: 1.2,
          py: 0.6,
          fontWeight: 800,
          fontSize: "0.95rem",
          textTransform: "none",
          "&:hover": { bgcolor: "#d97706 !important" },
        }}
        label={TT.edit}
      />
      <CustomDeleteButton record={record} />
    </div>
  );
};

/* ---------- Akıllı Başlık (dil öncelikli) ---------- */
const BaslikField = ({ label }) => {
  const r = useRecordContext();
  if (!r) return null;
  const pick = (code) =>
    code === "tr" ? r.baslik_tr : code === "en" ? r.baslik_en : r.baslik_de;
  const order = [r.dil, "tr", "en", "de"].filter(Boolean);
  let value = "-";
  for (const c of order) {
    const v = pick(c);
    if (v && v.trim() && v.trim() !== PLACEHOLDER) {
      value = v;
      break;
    }
  }
  return (
    <span
      style={{ fontWeight: 800, fontSize: "1.02rem", color: "#0f172a", display: "block" }}
      aria-label={label}
      title={value}
    >
      {value}
    </span>
  );
};

/* ---------- Dil etiketi ---------- */
const DilField = ({ label, names }) => {
  const r = useRecordContext();
  if (!r) return null;
  let code = r.dil;
  if (!code) {
    if (r.baslik_tr && r.baslik_tr !== PLACEHOLDER) code = "tr";
    else if (r.baslik_en && r.baslik_en !== PLACEHOLDER) code = "en";
    else if (r.baslik_de && r.baslik_de !== PLACEHOLDER) code = "de";
  }
  const text = names[code] || "-";
  return (
    <span aria-label={label} title={text} style={{ fontWeight: 700, fontSize: "0.95rem", color: "#334155" }}>
      {text}
    </span>
  );
};

/* ---------- Empty State (karşılama) ---------- */
const EmptyState = () => {
  const TT = useUiTexts();
  return (
    <div
      style={{
        width: "100%",
        minHeight: "50vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 680, padding: "32px 16px" }}>
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 14,
            border: "2px dashed #cbd5e1",
            margin: "0 auto 14px",
            display: "grid",
            placeItems: "center",
            fontSize: 36,
            color: "#94a3b8",
          }}
        >
          🗂️
        </div>
        <h2 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: "#0f172a" }}>
          {TT.emptyTitle}
        </h2>
        <p style={{ fontSize: 16, color: "#475569", marginTop: 10 }}>
          {TT.emptyDesc}
        </p>
        <CreateButton
          label={TT.emptyBtn}
          icon={<AddCircleIcon />}
          sx={{
            mt: 2,
            px: 3,
            py: 1.35,
            fontSize: "1.05rem",
            fontWeight: 800,
            borderRadius: "6px",
            textTransform: "none",
            bgcolor: "#f59e0b !important",
            color: "#fff !important",
            "&:hover": { bgcolor: "#d97706 !important" },
          }}
        />
      </div>
    </div>
  );
};

/* =================== LIST =================== */
export const AnaKategorilerPage = (props) => {
  const TT = useUiTexts();
  return (
    <>
      <GlobalStyles
        styles={{
          ".MuiSnackbarContent-message": { fontSize: "1.05rem", fontWeight: 800 },
          ".RaNotification-content": { fontSize: "1.05rem", fontWeight: 800 },
        }}
      />
      <List
        {...props}
        resource="anakategoriler"
        title={TT.listTitle}
        actions={<ListActions />}
        sort={{ field: "sira", order: "ASC" }}
        empty={<EmptyState />}
        sx={{
          m: 0,
          p: 0,
          width: "100%",
          maxWidth: "100%",
          "& .RaList-content": { p: 0, m: 0 },
          "& .MuiCard-root": { boxShadow: "none", borderRadius: 0 },
          "& .MuiContainer-root": {
            maxWidth: "100% !important",
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        <Datagrid
          rowClick="edit"
          bulkActionButtons={false}
          sx={{
            width: "100%",
            tableLayout: "fixed",
            "& .RaDatagrid-headerCell": {
              fontWeight: 900,
              fontSize: "1rem",
              color: "#0f172a",
              py: 1,
            },
            "& .RaDatagrid-row": {
              py: 0.75,
              "&:hover": { backgroundColor: "#f8fafc" },
            },
            "& .MuiTableCell-root": {
              paddingTop: "8px",
              paddingBottom: "8px",
              paddingLeft: "12px",
              paddingRight: "12px",
              verticalAlign: "middle",
            },
            "& .column-resim": {
              width: 100,
              minWidth: 150,
              maxWidth: 100,
              paddingRight: "15px",
            },
            "& .column-baslik": { width: "30%" },
            
            "& .column-sira": { width: 110, minWidth: 110 },
            "& .column-aktif": { width: 90, minWidth: 90 },
            "& .column-lang": { width: 120, minWidth: 120 },
            "& .column-actions": { width: 200, minWidth: 200 },
          }}
        >

         <FunctionField
  label={TT.image}
  className="column-resim"
  render={(r) =>
    r?.resim ? (
      <img
        src={toPublicUrl(r.resim)}
        alt=""
        style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb" }}
      />
    ) : null
  }
/>

          <BaslikField label={TT.title} className="column-baslik" />
          <TextField
           
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "0.95rem",
              color: "#0f172a",
            }}
          />
          <FunctionField
            label={TT.order}
            className="column-sira"
            render={(r) => (r?.sira ?? "-")}
          />
          <BooleanField source="aktif" label={TT.status} className="column-aktif" />
          <DilField label={TT.contentLang} names={TT.langNames} className="column-lang" />
          <div className="column-actions">
            <ActionButtons />
          </div>
        </Datagrid>
      </List>
    </>
  );
};

/* =================== EDIT =================== */
export const AnaKategoriEdit = (props) => (
  <EditBase resource="anakategoriler" {...props}>
    <AnaKategoriEditView />
  </EditBase>
);

function AnaKategoriEditView() {
  const TT = useUiTexts();
  const notify = useNotify();
  const redirect = useRedirect();
  const { record, isLoading } = useEditContext();
  const [update, { isLoading: saving }] = useUpdate();

  const [form, setForm] = React.useState({
    baslik_tr: "",
    baslik_en: "",
    baslik_de: "",
    aciklama_tr: "",
    aciklama_en: "",
    aciklama_de: "",
    resim: "",
    link: "",
    aktif: true,
    dil: "tr",
    sira: 0,
  });

  React.useEffect(() => {
    if (!isLoading && record) {
      setForm({
        baslik_tr: record.baslik_tr || "",
        baslik_en: record.baslik_en || "",
        baslik_de: record.baslik_de || "",
        aciklama_tr: record.aciklama_tr || "",
        aciklama_en: record.aciklama_en || "",
        aciklama_de: record.aciklama_de || "",
resim: extractUploadsPath(record.resim || ""),
       
        aktif: !!record.aktif,
        dil: record.dil || "tr",
        sira: Number(record.sira) || 0,
      });
    }
  }, [isLoading, record]);

  const onChange = (e) =>
    setForm((p) => ({
      ...p,
      [e.target.name]:
        e.target.name === "sira" ? Number(e.target.value) : e.target.value,
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!record) return;

   const merged = {
  ...record,
  ...form,
  resim: extractUploadsPath(form.resim),
  aktif: !!form.aktif,
  sira: Number(form.sira) || 0,
};


    update(
      "anakategoriler",
      { id: record.id, data: merged, previousData: record },
      {
        onSuccess: () => {
          notify(TT.updatedOk, { type: "success" });
          redirect("/anakategoriler");
        },
        onError: (e) =>
          notify(`❌ Güncelleme hatası: ${e?.message || ""}`, {
            type: "warning",
          }),
      }
    );
  };

  if (isLoading || !record) {
    return <div className="p-6">Yükleniyor…</div>;
  }

  return (
    <>
      <GlobalStyles
        styles={{
          ".MuiSnackbarContent-message": { fontSize: "1.05rem", fontWeight: 800 },
          ".RaNotification-content": { fontSize: "1.05rem", fontWeight: 800 },
        }}
      />
      <div className="w-full">
        <form onSubmit={handleSubmit} className="w-full bg-white px-6 py-5">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              alignItems: "start",
            }}
          >
            {/* Sol sütun */}
            <div className="space-y-4">
              <div>
                <label className="block font-medium mb-1">{TT.title}</label>
                <input
                  name={`baslik_${form.dil}`}
                  value={form[`baslik_${form.dil}`] || ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      [`baslik_${form.dil}`]: e.target.value,
                    }))
                  }
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block font-medium mb-1">
                  {TT.trDesc.replace("🇹🇷 ", "").replace(" (Türkçe)", "")}
                </label>
                <textarea
                  name={`aciklama_${form.dil}`}
                  value={form[`aciklama_${form.dil}`] || ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      [`aciklama_${form.dil}`]: e.target.value,
                    }))
                  }
                  className="w-full border rounded px-3 py-2"
                  rows={6}
                  required
                />
              </div>
</div>
             

            {/* Sağ sütun */}
            <div className="space-y-4">
              <div>
                <label className="block font-medium mb-1">{TT.image}</label>
                

<ResimUpload
  label={TT.uploadImage}
  onUpload={(url) => setForm((p) => ({ ...p, resim: extractUploadsPath(url) }))}
/>


{form.resim && (
  <img
    src={toPublicUrl(form.resim)}
    alt="Önizleme"
    className="mt-2 rounded shadow max-h-48"
  />
)}



              </div>

              <div>
                <label className="block font-medium mb-1">{TT.order}</label>
                <input
                  type="number"
                  name="sira"
                  value={form.sira}
                  onChange={onChange}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block font-medium mb-1">
                  {TT.contentLangShort}
                </label>
                <select
                  name="dil"
                  value={form.dil}
                  onChange={onChange}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="tr">{T.tr.langNames.tr}</option>
                  <option value="en">{T.tr.langNames.en}</option>
                  <option value="de">{T.tr.langNames.de}</option>
                </select>
              </div>

              <div className="pt-2 flex items-center gap-8">
                <button
                  type="button"
                  onClick={() => redirect("/anakategoriler")}
                  className="px-6 py-2 rounded text-white"
                  style={{ backgroundColor: "#f59e0b" }}
                >
                  {TT.cancel}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 rounded text-white disabled:opacity-60"
                  style={{ backgroundColor: "#2563eb" }}
                >
                  {saving ? TT.saving : TT.save}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

/* =================== CREATE =================== */
const AnaKategorilerCard = () => {
  const TT = useUiTexts();
  const [formData, setFormData] = React.useState({
    baslik_tr: "",
    baslik_en: "",
    baslik_de: "",
    aciklama_tr: "",
    aciklama_en: "",
    aciklama_de: "",
 
    resim: "",
    durum: "Aktif",
    dil: "tr",
    sira: 0,
  });

  const [aktifDiller, setAktifDiller] = React.useState(["tr", "en", "de"]);
  const notify = useNotify();
  const redirect = useRedirect();

  useEffect(() => {
    // aktif diller / varsayılan dil (opsiyonel: kurum ayarlarından çekmek isterseniz)
    const appLang = (localStorage.getItem("app_lang") || "tr").slice(0, 2).toLowerCase();
    if (!aktifDiller.includes(appLang)) return;
    setFormData((p) => ({ ...p, dil: appLang }));
  }, []);

  const handleChange = (e) =>
    setFormData((p) => ({
      ...p,
      [e.target.name]:
        e.target.name === "sira" ? Number(e.target.value) : e.target.value,
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const aktif = formData.durum === "Aktif";
    const payload = {
      baslik_tr: formData.dil === "tr" ? formData.baslik_tr : PLACEHOLDER,
      baslik_en: formData.dil === "en" ? formData.baslik_en : PLACEHOLDER,
      baslik_de: formData.dil === "de" ? formData.baslik_de : PLACEHOLDER,
      aciklama_tr: formData.dil === "tr" ? formData.aciklama_tr : PLACEHOLDER,
      aciklama_en: formData.dil === "en" ? formData.aciklama_en : PLACEHOLDER,
      aciklama_de: formData.dil === "de" ? formData.aciklama_de : PLACEHOLDER,
resim: extractUploadsPath(formData.resim),
    
      aktif,
      dil: formData.dil,
      sira: Number(formData.sira) || 0,
    };

    try {
      const token = localStorage.getItem("token");
const res = await fetch(`${API_BASE}/anakategoriler`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        notify(TT.createdOk, { type: "success" });
        redirect("/anakategoriler");
      } else {
        notify("❌ Kayıt sırasında hata oluştu", { type: "error" });
      }
    } catch (err) {
      console.error("Sunucu hatası:", err);
      notify("❌ Sunucu hatası", { type: "error" });
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          ".MuiSnackbarContent-message": { fontSize: "1.05rem", fontWeight: 800 },
          ".RaNotification-content": { fontSize: "1.05rem", fontWeight: 800 },
        }}
      />
      <div className="p-6 w/full bg-white rounded shadow ml-0">
        <h2 className="text-xl font-semibold mb-4">{TT.createTitle}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              alignItems: "start",
            }}
          >
            {/* Sol sütun */}
            <div className="space-y-4">
              <div>
                <label className="block font-medium mb-1">{TT.title}</label>
                <input
                  name={`baslik_${formData.dil}`}
                  value={formData[`baslik_${formData.dil}`] || ""}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block font-medium mb-1">
                  {TT.trDesc.replace("🇹🇷 ", "").replace(" (Türkçe)", "")}
                </label>
                <textarea
                  name={`aciklama_${formData.dil}`}
                  value={formData[`aciklama_${formData.dil}`] || ""}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  rows={10}
                  required
                />
              </div>
</div>
              

            {/* Sağ sütun */}
            <div className="space-y-4">
              <div>
                <label className="block font-medium mb-1">{TT.image}</label>
                

<ResimUpload
  onUpload={(url) => setFormData((p) => ({ ...p, resim: extractUploadsPath(url) }))}
  defaultPreview={toPublicUrl(formData.resim)}
  label={TT.uploadImage}
/>


              </div>

              <div>
                <label className="block font-medium mb-1">{TT.order}</label>
                <input
                  type="number"
                  name="sira"
                  value={formData.sira}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block font-medium mb-1">{TT.contentLang}</label>
                <select
                  name="dil"
                  value={formData.dil}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                >
                  {ALLOWED.includes("tr") && <option value="tr">{TT.langNames.tr}</option>}
                  {ALLOWED.includes("en") && <option value="en">{TT.langNames.en}</option>}
                  {ALLOWED.includes("de") && <option value="de">{TT.langNames.de}</option>}
                </select>
              </div>

              <div>
                <label className="block font-medium mb-1">{TT.status}</label>
                <select
                  name="durum"
                  value={formData.durum}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="Aktif">{TT.active}</option>
                  <option value="Pasif">{TT.passive}</option>
                </select>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
                >
                  {TT.save}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export const AnaKategoriCreate = () => <AnaKategorilerCard />;
